/* eslint-disable jsx-a11y/media-has-caption */
import { detect } from 'detect-browser';
import platform from 'platform';
import type { MutableRefObject } from 'react';
import { useEffect, useRef } from 'react';
import { useReactMediaRecorder } from 'react-media-recorder';
import { useSelector } from 'react-redux';

import { selectUser } from '../../features/auth/authSlice';
import {
  useSendMediaMutation,
  useSendWebcamLogMutation,
} from '../../features/exam/certificationApiSlice';
import {
  EXAM_AUDIO_CONSTRAINTS,
  EXAM_VIDEO_CONSTRAINTS,
  buildExamMediaRecorderOptions,
  resolveUploadFilename,
} from '../../features/exam/utils/mediaConstraints';
import { sleep } from '../../hooks/sleep';
import { VideoPreview } from '../VideoPreview';
import { supportsMediaConstraintInspection } from './mediaConstraintSupport';
import { resolveStablePreviewStream } from './resolveStablePreviewStream';

export type CameraControlHandle = {
  beginLivenessChallenge: (challengeType?: string) => void;
  endLivenessChallenge: (challengeType?: string) => void;
};

export type CameraProps = {
  type: 'video' | 'photo' | 'screen' | 'none' | 'video_liveness';
  certification_log?: string;
  captureDuration?: number; // in seconds
  captureInterval?: number; // in minutes
  onStreamReady?: (stream: MediaStream) => void;
  isExamFinalized?: boolean;
  isLivenessChallengeActive?: boolean;
  previewWidth?: number | string;
  previewHeight?: number | string;
  roundedPreview?: boolean;
  className?: string;
  controlRef?: MutableRefObject<CameraControlHandle | null>;
};

function getCameraPlaceholderMessage(
  status: string,
  hasBrowserSupport: boolean,
) {
  if (!hasBrowserSupport) {
    return 'Pré-visualização indisponível neste navegador.';
  }

  if (status === 'acquiring_media') {
    return 'Solicitando câmera e microfone...';
  }

  if (status === 'recording') {
    return 'Preparando preview ao vivo...';
  }

  return 'Conectando sua câmera ao monitoramento...';
}

export function CameraComponent({
  type,
  certification_log,
  captureDuration = 15, // time in seconds
  captureInterval = 2, // time in minutes
  onStreamReady,
  isExamFinalized = false,
  isLivenessChallengeActive = false,
  previewWidth = 150,
  previewHeight = 150,
  roundedPreview = true,
  className = '',
  controlRef,
}: CameraProps) {
  const user = useSelector(selectUser);
  const browser = detect();
  const [sendMedia] = useSendMediaMutation();
  const [registerWebcamLog] = useSendWebcamLogMutation();
  const isCapturingRef = useRef(false);
  const logCameraDebugRef = useRef<
    (event: string, details?: Record<string, unknown>) => void
  >(() => undefined);
  const isLivenessChallengeActiveRef = useRef(isLivenessChallengeActive);
  isLivenessChallengeActiveRef.current = isLivenessChallengeActive;
  const periodicCaptureMetaRef = useRef<{
    skipUpload: boolean;
    captureRef: string | null;
  }>({
    skipUpload: false,
    captureRef: null,
  });
  const inFlightPeriodicUploadRefs = useRef<Set<string>>(new Set());
  const suppressedPeriodicUploadRefs = useRef<Set<string>>(new Set());
  // Per-instance timer reference. Previously this was a module-level `let`,
  // which meant multiple Camera instances (or a remount) would share and
  // overwrite the same handle, leaking timers and triggering duplicate
  // periodic uploads.
  const recordTimeOutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const captureDurationMs = captureDuration * 1000;
  const captureIntervalMs = captureInterval * 60 * 1000;

  const uploadMedia = async (blob: Blob, captureRef?: string | null) => {
    if (type === 'video_liveness' && isLivenessChallengeActiveRef.current) {
      logCameraDebugRef.current('upload_skipped_liveness_active', {
        captureRef,
        blobSize: blob.size,
      });
      return;
    }

    if (type === 'video_liveness' && captureRef) {
      inFlightPeriodicUploadRefs.current.add(captureRef);
    }

    try {
      const extra = {
        session: 'webcam',
        type: type === 'video_liveness' ? 'video' : type,
        ...(certification_log && { certification_log }),
        ...(platform.description && { device_info: platform.description }),
        ...(user?._id && { player: user._id }),
      };

      const formData = new FormData();
      formData.append('file', blob, resolveUploadFilename(blob));
      formData.append('extra', JSON.stringify(extra));
      logCameraDebugRef.current('upload_start', {
        captureRef,
        blobSize: blob.size,
        session: extra.session,
      });
      const result = await sendMedia(formData).unwrap();
      if (result.status === 'OK' && result.uploads.length > 0) {
        const upload = result.uploads[0];
        if (upload) {
          const shouldDropCompletedPeriodicUpload =
            type === 'video_liveness' &&
            captureRef &&
            suppressedPeriodicUploadRefs.current.has(captureRef);

          logCameraDebugRef.current('upload_success', {
            captureRef,
            blobSize: blob.size,
            uploadUrl: upload.url,
            shouldDropCompletedPeriodicUpload,
          });

          if (shouldDropCompletedPeriodicUpload) {
            logCameraDebugRef.current(
              'upload_result_dropped_after_challenge_start',
              {
                captureRef,
                blobSize: blob.size,
                uploadUrl: upload.url,
              },
            );
            return;
          }

          registerWebcamLog({ time: new Date(), url: upload.url });
        }
      }
    } catch (err) {
      logCameraDebugRef.current('upload_error', {
        captureRef,
        blobSize: blob.size,
        error: err instanceof Error ? err.message : String(err),
      });
    } finally {
      if (captureRef) {
        inFlightPeriodicUploadRefs.current.delete(captureRef);
        suppressedPeriodicUploadRefs.current.delete(captureRef);
      }
    }
  };

  const { status, startRecording, stopRecording, previewStream } =
    useReactMediaRecorder({
      video: supportsMediaConstraintInspection()
        ? EXAM_VIDEO_CONSTRAINTS
        : true,
      audio: EXAM_AUDIO_CONSTRAINTS,
      mediaRecorderOptions: buildExamMediaRecorderOptions(),
      askPermissionOnMount: true,
      stopStreamsOnStop: false,
      onStop(_blobUrl, blob) {
        isCapturingRef.current = false;
        const { captureRef, skipUpload } = periodicCaptureMetaRef.current;
        if (!captureRef) {
          logCameraDebugRef.current('orphan_on_stop_ignored', {
            blobSize: blob.size,
            livenessChallengeActive: isLivenessChallengeActiveRef.current,
          });
          return;
        }

        const shouldSkipPeriodicUpload =
          type === 'video_liveness' &&
          (skipUpload || isLivenessChallengeActiveRef.current);

        periodicCaptureMetaRef.current = {
          skipUpload: false,
          captureRef: null,
        };

        logCameraDebugRef.current('on_stop', {
          captureRef,
          blobSize: blob.size,
          shouldSkipPeriodicUpload,
          livenessChallengeActive: isLivenessChallengeActiveRef.current,
        });

        if (shouldSkipPeriodicUpload) {
          if (captureRef) {
            suppressedPeriodicUploadRefs.current.delete(captureRef);
          }
          logCameraDebugRef.current('periodic_upload_dropped', {
            captureRef,
            blobSize: blob.size,
          });
          return;
        }

        uploadMedia(blob, captureRef);
      },
    });
  const statusRef = useRef(status);
  statusRef.current = status;

  // Keep stable refs so effects always call the latest function references.
  // react-media-recorder recreates startRecording/stopRecording on every render
  // (they are not wrapped in useCallback inside the library), so we must NOT
  // put them in useEffect deps — otherwise the capture cycle restarts on every
  // render, producing rapid uploads and hiding the preview.
  const startRecordingRef = useRef(startRecording);
  startRecordingRef.current = startRecording;
  const stopRecordingRef = useRef(stopRecording);
  stopRecordingRef.current = stopRecording;

  // Stabilise previewStream: useReactMediaRecorder returns `new MediaStream(...)`
  // on every render, causing VideoPreview to reset srcObject repeatedly.
  // We keep the current stream reference until the underlying video track changes
  // or the existing track becomes inactive.
  const stableStreamRef = useRef<MediaStream | null>(null);
  stableStreamRef.current = resolveStablePreviewStream(
    stableStreamRef.current,
    previewStream,
  );
  const stableStream = stableStreamRef.current;
  const placeholderMessage = getCameraPlaceholderMessage(
    status,
    Boolean(browser),
  );
  const resolvedPreviewWidth =
    typeof previewWidth === 'number' ? `${previewWidth}px` : previewWidth;
  const resolvedPreviewHeight =
    typeof previewHeight === 'number' ? `${previewHeight}px` : previewHeight;

  useEffect(() => {
    const statusUpdate = window.localStorage.getItem('certifica-updated');
    if (statusUpdate !== 'false')
      window.localStorage.setItem('certifica-updated', 'false');
  }, []);

  useEffect(() => {
    const videoTrack = stableStream?.getVideoTracks?.()[0];
    if (
      stableStream &&
      videoTrack &&
      videoTrack.readyState !== 'ended' &&
      onStreamReady
    ) {
      onStreamReady(stableStream);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stableStream]);

  // Stop any in-progress recording when the exam finalizes so the last clip is uploaded.
  useEffect(() => {
    if (!controlRef) {
      return undefined;
    }

    controlRef.current = {
      beginLivenessChallenge(challengeType) {
        isLivenessChallengeActiveRef.current = true;
        periodicCaptureMetaRef.current.skipUpload = true;
        const activeCaptureRef = periodicCaptureMetaRef.current.captureRef;
        if (activeCaptureRef) {
          suppressedPeriodicUploadRefs.current.add(activeCaptureRef);
        }
        inFlightPeriodicUploadRefs.current.forEach(captureRef => {
          suppressedPeriodicUploadRefs.current.add(captureRef);
        });
        logCameraDebugRef.current('imperative_begin_liveness_challenge', {
          challengeType,
          captureRef: activeCaptureRef,
          suppressedInFlightUploads: inFlightPeriodicUploadRefs.current.size,
          status: statusRef.current,
        });

        if (
          type === 'video_liveness' &&
          (isCapturingRef.current || statusRef.current === 'recording')
        ) {
          stopRecordingRef.current();
          isCapturingRef.current = false;
        }
      },
      endLivenessChallenge(challengeType) {
        isLivenessChallengeActiveRef.current = false;
        logCameraDebugRef.current('imperative_end_liveness_challenge', {
          challengeType,
        });
      },
    };

    return () => {
      if (controlRef.current) {
        controlRef.current = null;
      }
    };
  }, [controlRef, type]);

  useEffect(() => {
    if (type === 'video_liveness' && isLivenessChallengeActive) {
      // Any periodic webcam capture overlapping the challenge must be dropped,
      // otherwise the UI shows a duplicate unlabeled clip beside the
      // liveness-tagged clip.
      periodicCaptureMetaRef.current.skipUpload = true;
      logCameraDebugRef.current('mark_skip_upload_for_challenge');
    }

    if (
      (isExamFinalized ||
        (type === 'video_liveness' && isLivenessChallengeActive)) &&
      status === 'recording'
    ) {
      logCameraDebugRef.current('force_stop_recording', {
        reason: isExamFinalized
          ? 'exam_finalized'
          : 'liveness_challenge_active',
      });
      stopRecordingRef.current();
      isCapturingRef.current = false;
    }
  }, [isExamFinalized, isLivenessChallengeActive, status, type]);

  // Start the periodic capture cycle once on mount (runs for all monitoring types).
  // Empty deps so the effect does not restart when startRecording/stopRecording
  // get new references (they are recreated on every render by react-media-recorder).
  useEffect(() => {
    const captureVideo = async () => {
      if (isCapturingRef.current) return;
      if (isExamFinalized) return;

      if (type === 'video_liveness' && isLivenessChallengeActiveRef.current) {
        logCameraDebugRef.current('capture_delayed_due_liveness_active', {
          nextDelayMs: Math.min(captureIntervalMs, 5000),
        });
        if (recordTimeOutRef.current) clearTimeout(recordTimeOutRef.current);
        recordTimeOutRef.current = setTimeout(
          captureVideo,
          Math.min(captureIntervalMs, 5000),
        );
        return;
      }

      isCapturingRef.current = true;
      const captureRef = `periodic_${Date.now()}_${Math.random()
        .toString(16)
        .slice(2, 8)}`;
      periodicCaptureMetaRef.current = {
        skipUpload: false,
        captureRef,
      };
      logCameraDebugRef.current('capture_start', {
        captureRef,
        captureDurationMs,
      });

      startRecordingRef.current();
      await sleep(captureDurationMs);

      const shouldStopCurrentCapture =
        isCapturingRef.current &&
        periodicCaptureMetaRef.current.captureRef === captureRef;

      if (shouldStopCurrentCapture) {
        stopRecordingRef.current();
        isCapturingRef.current = false;
        logCameraDebugRef.current('capture_stop_requested', { captureRef });
      } else {
        logCameraDebugRef.current('capture_stop_skipped', {
          captureRef,
          activeCaptureRef: periodicCaptureMetaRef.current.captureRef,
          isCapturing: isCapturingRef.current,
        });
      }

      if (recordTimeOutRef.current) clearTimeout(recordTimeOutRef.current);
      recordTimeOutRef.current = setTimeout(captureVideo, captureIntervalMs);
    };

    const timer = setTimeout(captureVideo, 6000);

    return () => {
      clearTimeout(timer);
      if (recordTimeOutRef.current) {
        clearTimeout(recordTimeOutRef.current);
        recordTimeOutRef.current = null;
      }
      stopRecordingRef.current();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (browser && stableStream) {
    return (
      <div className={className}>
        <VideoPreview
          width={previewWidth}
          height={previewHeight}
          stream={stableStream}
          rounded={roundedPreview}
        />
      </div>
    );
  }

  return (
    <div className={className}>
      <div
        className={`flex items-center justify-center overflow-hidden border border-slate-700/80 bg-slate-950 ${
          roundedPreview ? 'rounded-full' : 'rounded-2xl'
        }`}
        style={{
          height: resolvedPreviewHeight,
          width: resolvedPreviewWidth,
        }}
      >
        <div className="px-4 text-center">
          <span className="mx-auto mb-3 block h-2.5 w-2.5 animate-pulse rounded-full bg-amber-400" />
          <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">
            Câmera
          </p>
          <p className="mt-2 text-xs leading-5 text-slate-300">
            {placeholderMessage}
          </p>
        </div>
      </div>
    </div>
  );
}
